import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';

export const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then((m) => m.Login),
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./pages/login/forgot-password/forgot-password.component').then((m) => m.ForgotPassword),
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./pages/login/reset-password/reset-password.component').then((m) => m.ResetPassword),
  },
  {
    path: '',
    loadComponent: () => import('./layouts/dashboard-layout/dashboard-layout.component').then((m) => m.DashboardLayout),
    children: [
      // -- Career Module (Public Access without Login) --
      {
        path: 'career',
        children: [
          { path: '', redirectTo: 'company', pathMatch: 'full' },
          {
            path: 'company',
            loadComponent: () =>
              import('./pages/career/company-career/company-career.component').then(
                (m) => m.CompanyCareerComponent
              ),
          },
          {
            path: 'candidate',
            loadComponent: () =>
              import('./pages/career/candidate-career/candidate-career.component').then(
                (m) => m.CandidateCareerComponent
              ),
          },
        ],
      },

      // -- Protected Routes (Require Login) --
      {
        path: '',
        canActivate: [authGuard],
        children: [
          {
            path: 'dashboard',
            loadComponent: () => import('./pages/dashboard/dashboard.component').then((m) => m.Dashboard),
          },

          // -- Customer Management --
          {
            path: 'customers',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/customer-management/customer-list/customer-list.component').then((m) => m.CustomerList),
                canActivate: [roleGuard],
                data: { module: 'Customers' },
              },
              {
                path: 'new',
                loadComponent: () =>
                  import('./pages/customer-management/customer-form/customer-form.component').then((m) => m.CustomerForm),
                canActivate: [roleGuard],
                data: { module: 'Customers' },
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./pages/customer-management/customer-view/customer-view.component').then((m) => m.CustomerView),
                canActivate: [roleGuard],
                data: { module: 'Customers' },
              },
              {
                path: ':id/edit',
                loadComponent: () =>
                  import('./pages/customer-management/customer-form/customer-form.component').then((m) => m.CustomerForm),
                canActivate: [roleGuard],
                data: { module: 'Customers' },
              },
              // Placeholders for sidebar nested items
              {
                path: 'companies',
                loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon),
              },
              {
                path: 'documents',
                loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon),
              },
              {
                path: 'notes',
                loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon),
              }
            ]
          },

          // -- Project Management --
          {
            path: 'projects',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/project-management/project-list/project-list.component').then((m) => m.ProjectList),
                canActivate: [roleGuard],
                data: { module: 'Projects' },
              },
              {
                path: 'new',
                loadComponent: () =>
                  import('./pages/project-management/project-form/project-form.component').then((m) => m.ProjectForm),
                canActivate: [roleGuard],
                data: { module: 'Projects' },
              },
              {
                path: ':id/edit',
                loadComponent: () =>
                  import('./pages/project-management/project-form/project-form.component').then((m) => m.ProjectForm),
                canActivate: [roleGuard],
                data: { module: 'Projects' },
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./pages/project-management/planning/pages/project-steps/project-steps.component').then((m) => m.ProjectSteps),
                canActivate: [roleGuard],
                data: { module: 'Projects' },
              },
              // Placeholders for sidebar nested items
              { path: 'dashboard', loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon) },
              { path: 'tasks', loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon) },
              { path: 'testing', loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon) },
              { path: 'support', loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon) },
              { path: 'developers', loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon) },
              { path: 'milestones', loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon) },
              { path: 'files', loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon) },
              { path: 'activity', loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon) }
            ],
          },

          // -- HRM Management --
          {
            path: 'hrm',
            children: [
              {
                path: '',
                redirectTo: 'dashboard',
                pathMatch: 'full',
              },
              {
                path: 'dashboard',
                loadComponent: () =>
                  import('./pages/hrm/hrm-dashboard/hrm-dashboard.component').then((m) => m.HrmDashboardComponent),
              },
              {
                path: 'leaves',
                loadComponent: () =>
                  import('./pages/hrm/leave-management/leave-management.component').then((m) => m.LeaveManagementComponent),
                canActivate: [roleGuard],
                data: { module: 'Leaves' },
              },
              {
                path: 'leave-approvals',
                loadComponent: () =>
                  import('./pages/hrm/leave-management/leave-management.component').then((m) => m.LeaveManagementComponent),
                canActivate: [roleGuard],
                data: { module: 'Leave Approvals' },
              },
              {
                path: 'leave-types',
                loadComponent: () =>
                  import('./pages/hrm/leave-types/leave-types.component').then((m) => m.LeaveTypesComponent),
                canActivate: [roleGuard],
                data: { module: 'Leave Types' },
              },
              {
                path: 'swipe-correction',
                loadComponent: () =>
                  import('./pages/hrm/swipe-correction/swipe-correction.component').then((m) => m.SwipeCorrectionComponent),
                canActivate: [roleGuard],
                data: { module: 'Swipe Correction' },
              },
              {
                path: 'attendance-history',
                loadComponent: () =>
                  import('./pages/hrm/attendance-history/attendance-history.component').then((m) => m.AttendanceHistoryComponent),
                canActivate: [roleGuard],
                data: { module: 'Attendance History' },
              },
              {
                path: 'punch-approvals',
                loadComponent: () =>
                  import('./pages/hrm/punch-approval/punch-approval.component').then((m) => m.PunchApprovalComponent),
                canActivate: [roleGuard],
                data: { module: 'Punch Approvals' },
              },
              {
                path: 'salary',
                loadComponent: () =>
                  import('./pages/hrm/salary/salary.component').then((m) => m.SalaryComponent),
                canActivate: [roleGuard],
                data: { module: 'Salary' },
              },
              {
                path: 'salary-structure',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./pages/hrm/salary-structure/salary-structure.component').then((m) => m.SalaryStructureComponent),
                  },
                  {
                    path: 'new',
                    loadComponent: () =>
                      import('./pages/hrm/salary-structure/salary-structure-form.component').then((m) => m.SalaryStructureFormComponent),
                  },
                  {
                    path: ':id/edit',
                    loadComponent: () =>
                      import('./pages/hrm/salary-structure/salary-structure-form.component').then((m) => m.SalaryStructureFormComponent),
                  },
                ],
                canActivate: [roleGuard],
                data: { module: 'Salary Structure' },
              },
              {
                path: 'loans',
                loadComponent: () =>
                  import('./pages/hrm/loans/loans.component').then((m) => m.LoansComponent),
                canActivate: [roleGuard],
                data: { module: 'Loans' },
              },
              {
                path: 'advances',
                loadComponent: () =>
                  import('./pages/hrm/advances/advances.component').then((m) => m.AdvancesComponent),
                canActivate: [roleGuard],
                data: { module: 'Advance' },
              },
              {
                path: 'exit',
                loadComponent: () =>
                  import('./pages/hrm/exit/exit.component').then((m) => m.ExitComponent),
                canActivate: [roleGuard],
                data: { module: 'Exit' },
              },
              {
                path: 'employee-exit',
                loadComponent: () =>
                  import('./pages/hrm/employee-exit/employee-exit.component').then((m) => m.EmployeeExitComponent),
                canActivate: [roleGuard],
                data: { module: 'Exit Management' },
              },
              {
                path: 'kra',
                loadComponent: () =>
                  import('./pages/hrm/responsibilities-kra/responsibilities-kra.component').then((m) => m.ResponsibilitiesKraComponent),
                canActivate: [roleGuard],
                data: { module: 'Responsibilities & KRA' },
              },
              {
                path: 'kra-management',
                loadComponent: () =>
                  import('./pages/hrm/kra-management/kra-management.component').then((m) => m.KraManagementComponent),
                canActivate: [roleGuard],
                data: { module: 'KRA Management' },
              },
              {
                path: 'staff',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./pages/staff-management/staff-list/staff-list.component').then((m) => m.StaffList),
                  },
                  {
                    path: 'new',
                    loadComponent: () =>
                      import('./pages/staff-management/staff-form/staff-form.component').then((m) => m.StaffForm),
                  },
                  {
                    path: ':id/edit',
                    loadComponent: () =>
                      import('./pages/staff-management/staff-form/staff-form.component').then((m) => m.StaffForm),
                  },
                ],
                canActivate: [roleGuard],
                data: { module: 'Staff' },
              },
              {
                path: 'location-access',
                loadComponent: () =>
                  import('./pages/hrm/location-access/location-access.component').then((m) => m.LocationAccessComponent),
                canActivate: [roleGuard],
                data: { module: 'Location Access' },
              },
              {
                path: 'shifts',
                loadComponent: () =>
                  import('./pages/hrm/shift-management/shift-management.component').then((m) => m.ShiftManagementComponent),
                canActivate: [roleGuard],
                data: { module: 'Shift Management' },
              },
              {
                path: 'machine-mapping',
                loadComponent: () =>
                  import('./pages/hrm/machine-mapping/machine-mapping.component').then((m) => m.MachineMappingComponent),
                canActivate: [roleGuard],
                data: { module: 'Machine Mapping' },
              },
              {
                path: 'holidays',
                loadComponent: () =>
                  import('./pages/hrm/holiday-management/holiday-management.component').then((m) => m.HolidayManagementComponent),
                canActivate: [roleGuard],
                data: { module: 'Holidays' },
              },
              {
                path: 'hr-policy',
                loadComponent: () =>
                  import('./pages/hrm/hr-policy/hr-policy.component').then((m) => m.HrPolicyComponent),
                canActivate: [roleGuard],
                data: { module: 'HR Policy' },
              },
            ],
          },

          // -- Other Roots --

          {
            path: 'staff',
            canActivate: [roleGuard],
            data: { module: 'Staff Management' },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/staff-management/staff-list/staff-list.component').then((m) => m.StaffList),
              },
              {
                path: 'new',
                loadComponent: () =>
                  import('./pages/staff-management/staff-form/staff-form.component').then((m) => m.StaffForm),
              },
              {
                path: ':id/edit',
                loadComponent: () =>
                  import('./pages/staff-management/staff-form/staff-form.component').then((m) => m.StaffForm),
              },
            ],
          },
          {
            path: 'tasks',
            canActivate: [roleGuard],
            data: { module: 'Task Management' },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/task-management/my-task-list/my-task-list.component').then((m) => m.MyTaskList),
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./pages/task-management/my-task-detail/my-task-detail.component').then((m) => m.MyTaskDetail),
              },
            ],
          },
          {
            path: 'timesheet',
            canActivate: [roleGuard],
            data: { module: 'Timesheet' },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/task-management/timesheet/timesheet.component').then((m) => m.Timesheet),
              },
            ],
          },
          {
            path: 'timer-corrections',
            canActivate: [roleGuard],
            data: { module: 'Timer Correction Approvals' },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/task-management/timer-correction-approvals/timer-correction-approvals.component').then((m) => m.TimerCorrectionApprovals),
              },
            ],
          },
          {
            path: 'planning-approval',
            children: [
              {
                path: '',
                loadComponent: () => import('./pages/planning-approval/planning-approval-list/planning-approval-list.component').then((m) => m.PlanningApprovalListComponent),
              },
              {
                path: ':projectId',
                loadComponent: () => import('./pages/planning-approval/planning-approval-project/planning-approval-project.component').then((m) => m.PlanningApprovalProjectComponent),
              },
              {
                path: ':projectId/plans/:planId',
                loadComponent: () => import('./pages/planning-approval/planning-approval-plan/planning-approval-plan.component').then((m) => m.PlanningApprovalPlanComponent),
              }
            ],
            canActivate: [roleGuard],
            data: { module: 'Planning Approval' },
          },
          {
            path: 'roles',
            children: [
              {
                path: '',
                loadComponent: () => import('./pages/roles/roles-list/roles-list.component').then((m) => m.RolesListComponent),
              },
              {
                path: 'new',
                loadComponent: () => import('./pages/roles/role-form/role-form.component').then((m) => m.RoleFormComponent),
              },
              {
                path: ':id/edit',
                loadComponent: () => import('./pages/roles/role-form/role-form.component').then((m) => m.RoleFormComponent),
              }
            ],
            canActivate: [roleGuard],
            data: { module: 'Roles & Permission' },
          },
          {
            path: 'reports',
            loadComponent: () => import('./shared/components/coming-soon/coming-soon.component').then((m) => m.ComingSoon),
            canActivate: [roleGuard],
            data: { module: 'Reports' },
          },
          {
            path: 'profile',
            loadComponent: () => import('./pages/profile/profile.component').then((m) => m.Profile),
          },
          {
            path: 'categorymaster',
            loadComponent: () => import('./pages/category-master/category-master.component').then((m) => m.CategoryMasterComponent),
          },
          // Cleanup legacy paths if they are accessed
          { path: 'project-management/projects', redirectTo: 'projects', pathMatch: 'full' },
          { path: 'project-management/customers', redirectTo: 'customers', pathMatch: 'full' },
        ],
      },
    ],
  },
  { path: '**', redirectTo: 'login' },
];
